import { Box, ThemeProvider, Typography } from "@mui/material";
import { COLORS, contentTheme } from "assets";
import { StyledCard } from "components";
import { PropsWithChildren, ReactNode } from "react";

export interface AuthPageLayoutProps {
  title: string;
  subtitle?: string;
  footer?: ReactNode;
  maxWidth?: number;
  /** @deprecated kept for call-site compatibility; unused in centered layout */
  heroTitle?: string;
  /** @deprecated kept for call-site compatibility; unused in centered layout */
  heroSubtitle?: string;
}

const glowBase = {
  position: "absolute" as const,
  pointerEvents: "none" as const,
  userSelect: "none" as const,
  zIndex: 0
};

export default function AuthPageLayout({
  title,
  subtitle,
  footer,
  maxWidth = 400,
  children
}: PropsWithChildren<AuthPageLayoutProps>) {
  return (
    <ThemeProvider theme={contentTheme}>
      <Box
        width="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        px={{ xs: 2, sm: 2.5 }}
        sx={{
          position: "relative",
          height: "100dvh",
          maxHeight: "100dvh",
          overflow: "hidden",
          boxSizing: "border-box",
          backgroundColor: "#050508"
        }}
      >
        <Box
          component="img"
          src="/auth/glow-top-left.webp"
          alt=""
          aria-hidden
          sx={{
            ...glowBase,
            top: { xs: -80, md: -120 },
            left: { xs: -100, md: -160 },
            width: { xs: 420, md: 640 },
            height: "auto",
            opacity: 0.95
          }}
        />
        <Box
          component="img"
          src="/auth/glow-center.webp"
          alt=""
          aria-hidden
          sx={{
            ...glowBase,
            top: "50%",
            left: "50%",
            width: { xs: 480, md: 720 },
            height: "auto",
            transform: "translate(-50%, -48%)",
            opacity: 0.75
          }}
        />
        <Box
          component="img"
          src="/auth/glow-bottom-right.webp"
          alt=""
          aria-hidden
          sx={{
            ...glowBase,
            right: { xs: -120, md: -180 },
            bottom: { xs: -100, md: -140 },
            width: { xs: 420, md: 640 },
            height: "auto",
            opacity: 0.9
          }}
        />

        <Box
          position="relative"
          zIndex={1}
          width="100%"
          maxWidth={maxWidth}
          display="flex"
          flexDirection="column"
          alignItems="center"
          sx={{
            transform: { xs: "scale(0.9)", sm: "scale(0.94)" },
            transformOrigin: "center center"
          }}
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mb={2}
            sx={{ flexShrink: 0, width: "100%" }}
          >
            <Box
              component="img"
              src="/neuravue-logo.png"
              alt="NeuraVue"
              sx={{
                width: { xs: 200, sm: 240 },
                maxWidth: "100%",
                height: "auto",
                objectFit: "contain",
                display: "block"
              }}
            />
          </Box>

          <StyledCard
            sx={{
              width: "100%",
              p: { xs: 2.25, sm: 2.75 },
              borderRadius: `${COLORS.radius.lg}px`,
              bgcolor: COLORS.surface.card,
              border: `1px solid ${COLORS.surface.border}`,
              boxShadow: COLORS.shadow.card
            }}
          >
            <Box mb={2}>
              <Typography
                sx={{ color: COLORS.surface.primaryText, fontWeight: 700, fontSize: 22, lineHeight: 1.25 }}
              >
                {title}
              </Typography>
              {subtitle ? (
                <Typography
                  sx={{ color: COLORS.surface.secondaryText, fontSize: 13, mt: 0.5, lineHeight: 1.45 }}
                >
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
            {children}
            {footer ? <Box mt={2}>{footer}</Box> : null}
          </StyledCard>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
